import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDetail, allArticles } from '@/mocks/articles';
import { categories } from '@/mocks/home';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = allArticles.find((a) => a.slug === slug) || articleDetail;
  const category = categories.find((c) => c.slug === article.categorySlug);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91chigua.com' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.91chigua.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.91chigua.com/article/${article.slug}` },
    ],
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    dateModified: article.updateDate,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '91吃瓜网',
    },
  };

  return (
    <>
      <title>{article.title} - 91吃瓜网</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={`91吃瓜网,${article.category},91吃瓜爆料,吃瓜群众,娱乐八卦`} />
      <meta property="og:title" content={`${article.title} - 91吃瓜网`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.91chigua.com/article/${article.slug}`} />
      <meta property="og:image" content={article.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} - 91吃瓜网`} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.image} />
      <link rel="canonical" href={`https://www.91chigua.com/article/${article.slug}`} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <article>
            <section className="relative w-full h-[350px] md:h-[480px] overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/70"></div>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end px-4 md:px-6 pb-10 md:pb-14">
                <div className="max-w-4xl">
                  <nav className="flex items-center gap-2 text-sm mb-3 text-background-50/60" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                    <Link to={`/category/${article.categorySlug}`} className="hover:text-background-50 transition-colors">
                      {article.category}
                    </Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                    <span className="text-background-50/80">{article.title.slice(0, 15)}...</span>
                  </nav>
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs mb-3">
                    {article.category}
                  </span>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight mb-3">
                    {article.title}
                  </h1>
                  <p className="text-sm md:text-base text-background-50/70 max-w-2xl line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-background-50/50">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-refresh-line"></i>
                      更新于 {article.updateDate}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-8 md:py-12 bg-background-100">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <div className="bg-background-50 rounded-2xl p-6 md:p-10">
                  <div className="flex items-start gap-4 mb-8 pb-6 border-b border-background-200/70">
                    <div className="px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap">
                      <i className="ri-star-fill text-accent-500"></i>
                      9.6
                    </div>
                    <div>
                      <blockquote className="text-base md:text-lg text-foreground-800 leading-relaxed font-serif">
                        &ldquo;{article.excerpt}&rdquo;
                      </blockquote>
                      <div className="flex items-center gap-3 mt-4">
                        <div className="w-10 h-10 rounded-full bg-secondary-300 overflow-hidden">
                          <img
                            src="https://readdy.ai/api/search-image?query=Simple%20portrait%20avatar%2C%20warm%20neutral%20background%2C%20soft%20lighting%2C%20professional%20casual%20style%2C%20clean%20minimal%20headshot&width=60&height=60&seq=user-avatar-01&orientation=squarish"
                            alt={article.author}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                          <p className="text-xs text-foreground-500">91吃瓜网特约记者</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="prose-custom">
                    {article.content.length > 0 ? (
                      article.content.map((block, idx) => {
                        if (block.type === 'paragraph') {
                          return (
                            <p key={idx} className="text-base leading-relaxed text-foreground-800 mb-5">
                              {block.text}
                            </p>
                          );
                        }
                        if (block.type === 'heading') {
                          return (
                            <h2 key={idx} className="text-xl font-heading font-bold text-foreground-950 mt-8 mb-4">
                              {block.text}
                            </h2>
                          );
                        }
                        if (block.type === 'image') {
                          return (
                            <figure key={idx} className="my-6 -mx-6 md:-mx-10">
                              <img
                                src={block.src || ''}
                                alt={block.caption || ''}
                                className="w-full rounded-lg"
                                loading="lazy"
                              />
                              {block.caption && (
                                <figcaption className="text-center text-xs text-foreground-500 mt-2">
                                  {block.caption}
                                </figcaption>
                              )}
                            </figure>
                          );
                        }
                        return null;
                      })
                    ) : (
                      <div className="space-y-5">
                        <p className="text-base leading-relaxed text-foreground-800">
                          更多详细内容正在紧张整理中。本网记者已第一时间赶赴现场，将持续为您带来最新报道。
                        </p>
                        <p className="text-base leading-relaxed text-foreground-800">
                          请关注91吃瓜网后续更新，或通过我们的分类页面浏览更多相关爆料资讯。
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-background-200/70">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-sm hover:bg-foreground-50 transition-colors duration-200 whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line"></i>
                      更多{article.category}
                    </Link>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
                    >
                      返回首页
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {article.relatedVideos && article.relatedVideos.length > 0 && (
              <section className="bg-foreground-950 py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                  <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-10">
                    相关视频推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                    {article.relatedVideos.map((video) => (
                      <Link
                        key={video.id}
                        to={`/article/${article.slug}`}
                        className="group rounded-2xl overflow-hidden bg-background-50/5 hover:bg-background-50/10 transition-colors duration-300"
                      >
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={video.image}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/20"></div>
                          <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md bg-foreground-950/80 text-background-50 text-xs">
                            {video.duration}
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-accent-500/90 flex items-center justify-center">
                              <i className="ri-play-fill text-background-50 text-xl md:text-2xl ml-0.5"></i>
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm md:text-base font-medium text-background-50 line-clamp-2 leading-snug mb-2">
                            {video.title}
                          </h3>
                          <div className="flex items-center gap-2 text-xs text-background-50/40">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {video.views}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}